import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  CalendarToday as CalendarIcon,
  ArrowDownward as InwardIcon,
  ArrowUpward as OutwardIcon,
  SwapVert as AllMovementsIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  TableChart as TableIcon,
  Summarize as SummaryIcon,
  Warehouse as WarehouseIcon,
  LocalShipping as ShippedIcon,
  Science as QCSampleIcon,
  Replay as ReprocessIcon,
  Print as ReprintIcon,
  Inventory as RepackIcon,
  Factory as ProducedIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS } from '../config';
import { PRODUCTS, GRADES, BRANDS, PACK_SIZES } from '../utils/DROPDOWN_OPTIONS';

// Operation configurations
const OPERATIONS = {
  PRODUCED: { label: 'PRODUCED', flow: 'IN', color: '#2e7d32', bgColor: '#e8f5e9', icon: ProducedIcon },
  'REPACK-IN': { label: 'REPACK-IN', flow: 'IN', color: '#1565c0', bgColor: '#e3f2fd', icon: RepackIcon },
  'REPACK-OUT': { label: 'REPACK-OUT', flow: 'OUT', color: '#e65100', bgColor: '#fff3e0', icon: RepackIcon },
  'RE-PRINTING': { label: 'RE-PRINTING', flow: 'OUT', color: '#7b1fa2', bgColor: '#f3e5f5', icon: ReprintIcon },
  'RE-PROCESS': { label: 'RE-PROCESS', flow: 'OUT', color: '#c2185b', bgColor: '#fce4ec', icon: ReprocessIcon },
  'QC SAMPLE': { label: 'QC SAMPLE', flow: 'OUT', color: '#00838f', bgColor: '#e0f7fa', icon: QCSampleIcon },
  'SHIPPED': { label: 'SHIPPED', flow: 'OUT', color: '#5d4037', bgColor: '#efebe9', icon: ShippedIcon },
};

const DATE_SELECTION_TYPES = {
  SPECIFIC: 'specific',
  RANGE: 'range',
  UPTODATE: 'uptodate',
};

const FLOW_TYPES = {
  ALL: 'all',
  INWARD: 'inward',
  OUTWARD: 'outward',
};

const StockReport = () => {
  // Filter States
  const [dateSelectionType, setDateSelectionType] = useState(DATE_SELECTION_TYPES.SPECIFIC);
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [flowType, setFlowType] = useState(FLOW_TYPES.ALL);
  const [selectedOperations, setSelectedOperations] = useState(Object.keys(OPERATIONS));
  
  // Product Filters
  const [productFilter, setProductFilter] = useState('');
  const [navIdFilter, setNavIdFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [packSizeFilter, setPackSizeFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  
  // Search Filters
  const [traceIdFilter, setTraceIdFilter] = useState('');
  const [dayCodeFilter, setDayCodeFilter] = useState('');
  const [fclNoFilter, setFclNoFilter] = useState('');
  const [lotNoFilter, setLotNoFilter] = useState('');
  const [palletIdFilter, setPalletIdFilter] = useState('');
  
  // Inline Table Filters
  const [inlineFilters, setInlineFilters] = useState({
    date: '',
    flow: '',
    operation: '',
    product: '',
    navId: '',
    grade: '',
    packSize: '',
    brand: '',
    palletId: '',
    traceId: '',
    dayCode: '',
    lotNo: '',
    cartonsMin: '',
    cartonsMax: '',
    looseBagsMin: '',
    looseBagsMax: '',
    totalLbsMin: '',
    totalLbsMax: '',
  });
  
  // Data States
  const [reportData, setReportData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // View States
  const [viewMode, setViewMode] = useState('detailed'); // 'detailed' or 'summary'
  const [groupBy, setGroupBy] = useState('date'); // 'date', 'product', 'operation', 'none'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Fetch report data
  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        dateSelectionType,
        fromDate: fromDate.format('YYYY-MM-DD'),
        toDate: dateSelectionType === DATE_SELECTION_TYPES.RANGE ? toDate.format('YYYY-MM-DD') : fromDate.format('YYYY-MM-DD'),
        flowType,
        operations: selectedOperations.join(','),
        product: productFilter,
        navId: navIdFilter,
        grade: gradeFilter,
        packSize: packSizeFilter,
        brand: brandFilter,
        traceId: traceIdFilter,
        dayCode: dayCodeFilter,
        fclNo: fclNoFilter,
        lotNo: lotNoFilter,
        palletId: palletIdFilter,
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.STOCK_REPORT, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummaryData(response.data.summary || null);
      } else {
        setError(response.data.message || 'Failed to fetch report data');
      }
    } catch (err) {
      console.error('Error fetching stock report:', err);
      setError(err.response?.data?.message || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  }, [dateSelectionType, fromDate, toDate, flowType, selectedOperations, productFilter, navIdFilter, gradeFilter, packSizeFilter, brandFilter, traceIdFilter, dayCodeFilter, fclNoFilter, lotNoFilter, palletIdFilter]);

  // Reset filters
  const handleResetFilters = () => {
    setDateSelectionType(DATE_SELECTION_TYPES.SPECIFIC);
    setFromDate(dayjs());
    setToDate(dayjs());
    setFlowType(FLOW_TYPES.ALL);
    setSelectedOperations(Object.keys(OPERATIONS));
    setProductFilter('');
    setNavIdFilter('');
    setGradeFilter('');
    setPackSizeFilter('');
    setBrandFilter('');
    setTraceIdFilter('');
    setDayCodeFilter('');
    setFclNoFilter('');
    setLotNoFilter('');
    setPalletIdFilter('');
    setInlineFilters({
      date: '', flow: '', operation: '', product: '', navId: '', grade: '',
      packSize: '', brand: '', palletId: '', traceId: '', dayCode: '', lotNo: '',
      cartonsMin: '', cartonsMax: '', looseBagsMin: '', looseBagsMax: '', totalLbsMin: '', totalLbsMax: '',
    });
  };

  // Toggle operation selection
  const handleOperationToggle = (operation) => {
    setSelectedOperations(prev => {
      if (prev.includes(operation)) {
        return prev.filter(op => op !== operation);
      } else {
        return [...prev, operation];
      }
    });
  };

  // Update inline filter
  const handleInlineFilterChange = (field, value) => {
    setInlineFilters(prev => ({ ...prev, [field]: value }));
  };

  // Filter data based on inline filters
  const filteredData = useMemo(() => {
    return reportData.filter(row => {
      // Date filter
      if (inlineFilters.date && !row.date?.includes(inlineFilters.date)) return false;
      
      // Flow filter
      if (inlineFilters.flow && row.flow !== inlineFilters.flow) return false;
      
      // Operation filter
      if (inlineFilters.operation && row.operation !== inlineFilters.operation) return false;
      
      // Product filter
      if (inlineFilters.product && row.product !== inlineFilters.product) return false;
      
      // NAV ID filter
      if (inlineFilters.navId && !row.nav_id?.toLowerCase().includes(inlineFilters.navId.toLowerCase())) return false;
      
      // Grade filter
      if (inlineFilters.grade && row.grade !== inlineFilters.grade) return false;
      
      // Pack Size filter
      if (inlineFilters.packSize && row.pack_size !== inlineFilters.packSize) return false;
      
      // Brand filter
      if (inlineFilters.brand && !row.brand?.toLowerCase().includes(inlineFilters.brand.toLowerCase())) return false;
      
      // Pallet ID filter
      if (inlineFilters.palletId && !row.pallet_id?.toString().includes(inlineFilters.palletId)) return false;
      
      // Trace ID filter
      if (inlineFilters.traceId && !row.trace_id?.toLowerCase().includes(inlineFilters.traceId.toLowerCase())) return false;
      
      // Day Code filter
      if (inlineFilters.dayCode && !row.day_code?.toLowerCase().includes(inlineFilters.dayCode.toLowerCase())) return false;
      
      // Lot No filter
      if (inlineFilters.lotNo && !row.lot_no?.toLowerCase().includes(inlineFilters.lotNo.toLowerCase())) return false;
      
      // Cartons range filter
      const cartons = Math.abs(row.cartons || 0);
      if (inlineFilters.cartonsMin && cartons < parseFloat(inlineFilters.cartonsMin)) return false;
      if (inlineFilters.cartonsMax && cartons > parseFloat(inlineFilters.cartonsMax)) return false;
      
      // Loose Bags range filter
      const looseBags = Math.abs(row.loose_bags || 0);
      if (inlineFilters.looseBagsMin && looseBags < parseFloat(inlineFilters.looseBagsMin)) return false;
      if (inlineFilters.looseBagsMax && looseBags > parseFloat(inlineFilters.looseBagsMax)) return false;
      
      // Total LBS range filter
      const totalLbs = Math.abs(row.total_lbs || 0);
      if (inlineFilters.totalLbsMin && totalLbs < parseFloat(inlineFilters.totalLbsMin)) return false;
      if (inlineFilters.totalLbsMax && totalLbs > parseFloat(inlineFilters.totalLbsMax)) return false;
      
      return true;
    });
  }, [reportData, inlineFilters]);

  // Group data
  const groupedData = useMemo(() => {
    if (groupBy === 'none') return { 'All Data': filteredData };
    
    return filteredData.reduce((groups, row) => {
      let key;
      switch (groupBy) {
        case 'date':
          key = row.date;
          break;
        case 'product':
          key = row.product;
          break;
        case 'operation':
          key = row.operation;
          break;
        default:
          key = 'All Data';
      }
      
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
      return groups;
    }, {});
  }, [filteredData, groupBy]);

  // Calculate group totals
  const calculateGroupTotals = (rows) => {
    return rows.reduce((totals, row) => {
      const isInward = row.flow === 'IN';
      const cartons = Math.abs(row.cartons || 0);
      const looseBags = Math.abs(row.loose_bags || 0);
      const totalLbs = Math.abs(row.total_lbs || 0);
      
      if (isInward) {
        totals.inCartons += cartons;
        totals.inLooseBags += looseBags;
        totals.inLbs += totalLbs;
      } else {
        totals.outCartons += cartons;
        totals.outLooseBags += looseBags;
        totals.outLbs += totalLbs;
      }
      
      return totals;
    }, { inCartons: 0, outCartons: 0, inLooseBags: 0, outLooseBags: 0, inLbs: 0, outLbs: 0 });
  };

  // Export to Excel
  const handleExportExcel = () => {
    const exportData = filteredData.map(row => ({
      'Date': row.date,
      'Flow': row.flow,
      'Operation': row.operation,
      'Product': row.product,
      'NAV ID': row.nav_id,
      'Grade': row.grade,
      'Pack Size': row.pack_size,
      'Brand': row.brand,
      'Pallet ID': row.pallet_id,
      'Trace ID': row.trace_id,
      'Day Code': row.day_code,
      'Lot No': row.lot_no,
      'Cartons': row.cartons,
      'Loose Bags': row.loose_bags,
      'Total LBS': row.total_lbs,
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Report');
    XLSX.writeFile(wb, `Stock_Report_${dayjs().format('YYYY-MM-DD_HHmmss')}.xlsx`);
  };

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // Format number with sign
  const formatNumber = (value, isInward) => {
    const num = Math.abs(value || 0);
    const formatted = num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return isInward ? `+${formatted}` : `-${formatted}`;
  };

  // Get unique values for dropdowns
  const uniqueProducts = useMemo(() => [...new Set(reportData.map(r => r.product).filter(Boolean))], [reportData]);
  const uniqueGrades = useMemo(() => [...new Set(reportData.map(r => r.grade).filter(Boolean))], [reportData]);
  const uniquePackSizes = useMemo(() => [...new Set(reportData.map(r => r.pack_size).filter(Boolean))], [reportData]);
  const uniqueOperations = useMemo(() => [...new Set(reportData.map(r => r.operation).filter(Boolean))], [reportData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
        {/* Header */}
        <Paper sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white', boxShadow: '0 2px 14px rgba(0,0,0,.2)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarehouseIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">Stock Inward / Outward Report</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Cold Store Stock Movement Analysis</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Filter Section */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1, borderBottom: '2px solid #e0e0e0' }}>
            <FilterIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="600">Report Filters</Typography>
          </Box>

          {/* Date Selection Type */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Date Selection Type
            </Typography>
            <ToggleButtonGroup
              value={dateSelectionType}
              exclusive
              onChange={(e, val) => val && setDateSelectionType(val)}
              size="small"
              sx={{ mt: 0.5, display: 'flex', gap: 1 }}
            >
              <ToggleButton value={DATE_SELECTION_TYPES.SPECIFIC} sx={{ textTransform: 'none', px: 2 }}>
                <CalendarIcon sx={{ mr: 1, fontSize: 18 }} /> Specific Date
              </ToggleButton>
              <ToggleButton value={DATE_SELECTION_TYPES.RANGE} sx={{ textTransform: 'none', px: 2 }}>
                <CalendarIcon sx={{ mr: 1, fontSize: 18 }} /> Date Range
              </ToggleButton>
              <ToggleButton value={DATE_SELECTION_TYPES.UPTODATE} sx={{ textTransform: 'none', px: 2 }}>
                <CalendarIcon sx={{ mr: 1, fontSize: 18 }} /> Up to Date (Cumulative)
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Date Inputs */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            {dateSelectionType === DATE_SELECTION_TYPES.RANGE && (
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={setToDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
            )}
          </Grid>

          {/* Flow Type */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Stock Flow Type
            </Typography>
            <ToggleButtonGroup
              value={flowType}
              exclusive
              onChange={(e, val) => val && setFlowType(val)}
              size="small"
              sx={{ mt: 0.5, display: 'flex', gap: 1 }}
            >
              <ToggleButton value={FLOW_TYPES.ALL} sx={{ textTransform: 'none', px: 2 }}>
                <AllMovementsIcon sx={{ mr: 1, fontSize: 18 }} /> All Movements
              </ToggleButton>
              <ToggleButton value={FLOW_TYPES.INWARD} sx={{ textTransform: 'none', px: 2, color: '#4caf50', '&.Mui-selected': { bgcolor: '#4caf50', color: 'white' } }}>
                <InwardIcon sx={{ mr: 1, fontSize: 18 }} /> Inward Only
              </ToggleButton>
              <ToggleButton value={FLOW_TYPES.OUTWARD} sx={{ textTransform: 'none', px: 2, color: '#f44336', '&.Mui-selected': { bgcolor: '#f44336', color: 'white' } }}>
                <OutwardIcon sx={{ mr: 1, fontSize: 18 }} /> Outward Only
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Operations Selection */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Operations (Click to toggle)
            </Typography>
            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(OPERATIONS).map(([key, op]) => (
                <Chip
                  key={key}
                  icon={selectedOperations.includes(key) ? <op.icon /> : null}
                  label={`${op.label} (${op.flow})`}
                  onClick={() => handleOperationToggle(key)}
                  sx={{
                    bgcolor: selectedOperations.includes(key) ? op.color : op.bgColor,
                    color: selectedOperations.includes(key) ? 'white' : op.color,
                    fontWeight: 600,
                    '&:hover': { opacity: 0.8 },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Product Filters */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                select
                label="Product"
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">All Products</MenuItem>
                {PRODUCTS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="NAV ID"
                value={navIdFilter}
                onChange={(e) => setNavIdFilter(e.target.value)}
                size="small"
                fullWidth
                placeholder="Search NAV ID..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                select
                label="Grade"
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">All Grades</MenuItem>
                {GRADES.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                select
                label="Pack Size"
                value={packSizeFilter}
                onChange={(e) => setPackSizeFilter(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">All Pack Sizes</MenuItem>
                {PACK_SIZES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                select
                label="Brand"
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">All Brands</MenuItem>
                {BRANDS.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>

          {/* Search Filters */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Trace ID"
                value={traceIdFilter}
                onChange={(e) => setTraceIdFilter(e.target.value)}
                size="small"
                fullWidth
                placeholder="Search Trace ID..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Day Code"
                value={dayCodeFilter}
                onChange={(e) => setDayCodeFilter(e.target.value)}
                size="small"
                fullWidth
                placeholder="Search Day Code..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="FCL No"
                value={fclNoFilter}
                onChange={(e) => setFclNoFilter(e.target.value)}
                size="small"
                fullWidth
                placeholder="Search FCL No..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Lot No"
                value={lotNoFilter}
                onChange={(e) => setLotNoFilter(e.target.value)}
                size="small"
                fullWidth
                placeholder="Search Lot No..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Pallet ID"
                value={palletIdFilter}
                onChange={(e) => setPalletIdFilter(e.target.value)}
                size="small"
                fullWidth
                placeholder="Search Pallet ID..."
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleResetFilters}>
              Reset
            </Button>
            <Button variant="contained" color="success" startIcon={<ExportIcon />} onClick={handleExportExcel} disabled={!reportData.length}>
              Export Excel
            </Button>
            <Button variant="contained" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />} onClick={fetchReportData} disabled={loading}>
              Generate Report
            </Button>
          </Box>
        </Paper>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Dashboard Summary */}
        {summaryData && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarehouseIcon color="primary" /> Dashboard Summary - Stock Movement by Operation
            </Typography>

            {/* Date Info Banner */}
            <Paper sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white', display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Showing data for:</Typography>
                <Typography variant="h6" fontWeight="bold">
                  <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                  {dateSelectionType === DATE_SELECTION_TYPES.RANGE
                    ? `${fromDate.format('DD-MMM-YYYY')} to ${toDate.format('DD-MMM-YYYY')}`
                    : fromDate.format('DD-MMM-YYYY')}
                  {dateSelectionType === DATE_SELECTION_TYPES.UPTODATE && ' (Cumulative)'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Total Entries</Typography>
                <Typography variant="h6" fontWeight="bold">{summaryData.totalEntries?.toLocaleString()}</Typography>
              </Box>
            </Paper>

            {/* Operation Cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {Object.entries(OPERATIONS).map(([key, op]) => {
                const opData = summaryData.byOperation?.[key] || {};
                return (
                  <Grid item xs={12} sm={6} md={4} lg={2} key={key}>
                    <Card sx={{ height: '100%' }}>
                      <Box sx={{ p: 1.5, background: `linear-gradient(135deg, ${op.color} 0%, ${op.color}dd 100%)`, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <op.icon fontSize="small" /> {op.label}
                        </Typography>
                        <Chip label={op.flow === 'IN' ? '↓ INWARD' : '↑ OUTWARD'} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.65rem', height: 20 }} />
                      </Box>
                      <CardContent sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px dashed #eee' }}>
                          <Typography variant="caption" color="text.secondary">Cartons</Typography>
                          <Typography variant="body2" fontWeight="bold" color={op.flow === 'IN' ? 'success.main' : 'error.main'}>
                            {op.flow === 'IN' ? '+' : '-'}{(opData.cartons || 0).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px dashed #eee' }}>
                          <Typography variant="caption" color="text.secondary">Loose Bags</Typography>
                          <Typography variant="body2" fontWeight="bold" color={op.flow === 'IN' ? 'success.main' : 'error.main'}>
                            {op.flow === 'IN' ? '+' : '-'}{(opData.looseBags || 0).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px dashed #eee' }}>
                          <Typography variant="caption" color="text.secondary">Weight (LB)</Typography>
                          <Typography variant="body2" fontWeight="bold" color={op.flow === 'IN' ? 'success.main' : 'error.main'}>
                            {op.flow === 'IN' ? '+' : '-'}{(opData.totalLbs || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Weight (KG)</Typography>
                          <Typography variant="body2" fontWeight="bold" color={op.flow === 'IN' ? 'success.main' : 'error.main'}>
                            {op.flow === 'IN' ? '+' : '-'}{(opData.totalKg || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Cold Store Stock Summary */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarehouseIcon color="info" /> Total Cold Store Stock Summary
              </Typography>
              <Grid container spacing={2}>
                {/* Inward */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', border: '2px solid #a5d6a7' }}>
                    <Typography variant="body2" fontWeight="600" color="#2e7d32" sx={{ textAlign: 'center', mb: 1 }}>
                      <InwardIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} /> TOTAL INWARD
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Cartons:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#2e7d32">+{(summaryData.inward?.cartons || 0).toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Loose Bags:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#2e7d32">+{(summaryData.inward?.looseBags || 0).toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Weight (LB):</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#2e7d32">+{(summaryData.inward?.totalLbs || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Weight (KG):</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#2e7d32">+{(summaryData.inward?.totalKg || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                </Grid>
                {/* Outward */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', border: '2px solid #ef9a9a' }}>
                    <Typography variant="body2" fontWeight="600" color="#c62828" sx={{ textAlign: 'center', mb: 1 }}>
                      <OutwardIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} /> TOTAL OUTWARD
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Cartons:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#c62828">-{(summaryData.outward?.cartons || 0).toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Loose Bags:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#c62828">-{(summaryData.outward?.looseBags || 0).toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Weight (LB):</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#c62828">-{(summaryData.outward?.totalLbs || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Weight (KG):</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#c62828">-{(summaryData.outward?.totalKg || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                </Grid>
                {/* Net */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', border: '2px solid #90caf9' }}>
                    <Typography variant="body2" fontWeight="600" color="#1565c0" sx={{ textAlign: 'center', mb: 1 }}>
                      <AllMovementsIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} /> NET STOCK
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Cartons:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#1565c0">
                        {(summaryData.net?.cartons || 0) >= 0 ? '+' : ''}{(summaryData.net?.cartons || 0).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Loose Bags:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#1565c0">
                        {(summaryData.net?.looseBags || 0) >= 0 ? '+' : ''}{(summaryData.net?.looseBags || 0).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Weight (LB):</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#1565c0">
                        {(summaryData.net?.totalLbs || 0) >= 0 ? '+' : ''}{(summaryData.net?.totalLbs || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Weight (KG):</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#1565c0">
                        {(summaryData.net?.totalKg || 0) >= 0 ? '+' : ''}{(summaryData.net?.totalKg || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Data Table */}
        <Paper sx={{ overflow: 'hidden' }}>
          {/* Table Header */}
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fafafa', borderBottom: '1px solid #eee' }}>
            <Typography variant="subtitle1" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TableIcon color="primary" /> Stock Movement Details
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <ToggleButtonGroup value={viewMode} exclusive onChange={(e, val) => val && setViewMode(val)} size="small">
                <ToggleButton value="detailed"><TableIcon sx={{ mr: 0.5 }} /> Detailed</ToggleButton>
                <ToggleButton value="summary"><SummaryIcon sx={{ mr: 0.5 }} /> Summary</ToggleButton>
              </ToggleButtonGroup>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Group by</InputLabel>
                <Select value={groupBy} label="Group by" onChange={(e) => setGroupBy(e.target.value)}>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="product">Product</MenuItem>
                  <MenuItem value="operation">Operation</MenuItem>
                  <MenuItem value="none">None</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                {/* Header Row */}
                <TableRow>
                  {['Date', 'Flow', 'Operation', 'Product', 'NAV ID', 'Grade', 'Pack Size', 'Brand', 'Pallet ID', 'Trace ID', 'Day Code', 'Lot No', 'Cartons', 'Loose Bags', 'Total LBS'].map((header, idx) => (
                    <TableCell
                      key={header}
                      sx={{
                        background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        textAlign: idx >= 12 ? 'right' : 'left',
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
                
                {/* Inline Filter Row */}
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField size="small" placeholder="Search..." value={inlineFilters.date} onChange={(e) => handleInlineFilterChange('date', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }} />
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField select size="small" value={inlineFilters.flow} onChange={(e) => handleInlineFilterChange('flow', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }}>
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="IN">IN</MenuItem>
                      <MenuItem value="OUT">OUT</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField select size="small" value={inlineFilters.operation} onChange={(e) => handleInlineFilterChange('operation', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }}>
                      <MenuItem value="">All</MenuItem>
                      {uniqueOperations.map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField select size="small" value={inlineFilters.product} onChange={(e) => handleInlineFilterChange('product', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }}>
                      <MenuItem value="">All</MenuItem>
                      {uniqueProducts.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField size="small" placeholder="Search..." value={inlineFilters.navId} onChange={(e) => handleInlineFilterChange('navId', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }} />
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField select size="small" value={inlineFilters.grade} onChange={(e) => handleInlineFilterChange('grade', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }}>
                      <MenuItem value="">All</MenuItem>
                      {uniqueGrades.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField select size="small" value={inlineFilters.packSize} onChange={(e) => handleInlineFilterChange('packSize', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }}>
                      <MenuItem value="">All</MenuItem>
                      {uniquePackSizes.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField size="small" placeholder="Search..." value={inlineFilters.brand} onChange={(e) => handleInlineFilterChange('brand', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }} />
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField size="small" placeholder="Search..." value={inlineFilters.palletId} onChange={(e) => handleInlineFilterChange('palletId', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }} />
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField size="small" placeholder="Search..." value={inlineFilters.traceId} onChange={(e) => handleInlineFilterChange('traceId', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }} />
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField size="small" placeholder="Search..." value={inlineFilters.dayCode} onChange={(e) => handleInlineFilterChange('dayCode', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }} />
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <TextField size="small" placeholder="Search..." value={inlineFilters.lotNo} onChange={(e) => handleInlineFilterChange('lotNo', e.target.value)} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '5px 8px' } }} />
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <TextField size="small" placeholder="Min" value={inlineFilters.cartonsMin} onChange={(e) => handleInlineFilterChange('cartonsMin', e.target.value)} sx={{ width: '50%', '& .MuiInputBase-input': { fontSize: '0.7rem', p: '5px 4px', textAlign: 'center' } }} />
                      <TextField size="small" placeholder="Max" value={inlineFilters.cartonsMax} onChange={(e) => handleInlineFilterChange('cartonsMax', e.target.value)} sx={{ width: '50%', '& .MuiInputBase-input': { fontSize: '0.7rem', p: '5px 4px', textAlign: 'center' } }} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <TextField size="small" placeholder="Min" value={inlineFilters.looseBagsMin} onChange={(e) => handleInlineFilterChange('looseBagsMin', e.target.value)} sx={{ width: '50%', '& .MuiInputBase-input': { fontSize: '0.7rem', p: '5px 4px', textAlign: 'center' } }} />
                      <TextField size="small" placeholder="Max" value={inlineFilters.looseBagsMax} onChange={(e) => handleInlineFilterChange('looseBagsMax', e.target.value)} sx={{ width: '50%', '& .MuiInputBase-input': { fontSize: '0.7rem', p: '5px 4px', textAlign: 'center' } }} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5', p: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <TextField size="small" placeholder="Min" value={inlineFilters.totalLbsMin} onChange={(e) => handleInlineFilterChange('totalLbsMin', e.target.value)} sx={{ width: '50%', '& .MuiInputBase-input': { fontSize: '0.7rem', p: '5px 4px', textAlign: 'center' } }} />
                      <TextField size="small" placeholder="Max" value={inlineFilters.totalLbsMax} onChange={(e) => handleInlineFilterChange('totalLbsMax', e.target.value)} sx={{ width: '50%', '& .MuiInputBase-input': { fontSize: '0.7rem', p: '5px 4px', textAlign: 'center' } }} />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 1 }}>Loading report data...</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center" sx={{ py: 5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {reportData.length === 0 ? 'Click "Generate Report" to load data' : 'No matching records found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  Object.entries(groupedData).map(([groupKey, rows]) => (
                    <React.Fragment key={groupKey}>
                      {/* Group Header */}
                      {groupBy !== 'none' && (
                        <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                          <TableCell colSpan={15} sx={{ fontWeight: 700, color: '#3f51b5' }}>
                            <CalendarIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 18 }} />
                            <strong>{groupKey}</strong> — {rows.length} entries | 
                            <span style={{ color: '#4caf50', marginLeft: 8 }}>▼ IN: +{calculateGroupTotals(rows).inCartons} ctns</span> | 
                            <span style={{ color: '#f44336', marginLeft: 8 }}>▲ OUT: -{calculateGroupTotals(rows).outCartons} ctns</span>
                          </TableCell>
                        </TableRow>
                      )}
                      
                      {/* Data Rows */}
                      {rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((row, idx) => {
                        const isInward = row.flow === 'IN';
                        const opConfig = OPERATIONS[row.operation] || {};
                        
                        return (
                          <TableRow key={`${groupKey}-${idx}`} hover>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{row.date}</TableCell>
                            <TableCell>
                              <Chip
                                icon={isInward ? <InwardIcon /> : <OutwardIcon />}
                                label={row.flow}
                                size="small"
                                sx={{
                                  bgcolor: isInward ? '#e8f5e9' : '#ffebee',
                                  color: isInward ? '#2e7d32' : '#c62828',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row.operation}
                                size="small"
                                sx={{
                                  bgcolor: opConfig.bgColor || '#f5f5f5',
                                  color: opConfig.color || '#666',
                                  fontWeight: 600,
                                  fontSize: '0.68rem',
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{row.product}</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem' }}>{row.nav_id}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{row.grade}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{row.pack_size}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{row.brand}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{row.pallet_id}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{row.trace_id}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{row.day_code}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{row.lot_no}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: isInward ? '#2e7d32' : '#c62828', fontFamily: 'Consolas, monospace' }}>
                              {formatNumber(row.cartons, isInward)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: row.loose_bags ? (isInward ? '#2e7d32' : '#c62828') : 'inherit', fontFamily: 'Consolas, monospace' }}>
                              {row.loose_bags ? formatNumber(row.loose_bags, isInward) : '0'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: isInward ? '#2e7d32' : '#c62828', fontFamily: 'Consolas, monospace' }}>
                              {formatNumber(row.total_lbs, isInward)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
              
              {/* Footer with Totals */}
              {filteredData.length > 0 && (
                <TableFooter>
                  <TableRow sx={{ bgcolor: '#3f51b5' }}>
                    <TableCell colSpan={12} sx={{ color: 'white', fontWeight: 700, textAlign: 'right' }}>
                      PAGE TOTALS:
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, textAlign: 'right', fontFamily: 'Consolas, monospace' }}>
                      <span style={{ color: '#a5d6a7' }}>+{calculateGroupTotals(filteredData).inCartons}</span> / 
                      <span style={{ color: '#ef9a9a' }}> -{calculateGroupTotals(filteredData).outCartons}</span>
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, textAlign: 'right', fontFamily: 'Consolas, monospace' }}>
                      <span style={{ color: '#a5d6a7' }}>+{calculateGroupTotals(filteredData).inLooseBags}</span> / 
                      <span style={{ color: '#ef9a9a' }}> -{calculateGroupTotals(filteredData).outLooseBags}</span>
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, textAlign: 'right', fontFamily: 'Consolas, monospace' }}>
                      <span style={{ color: '#a5d6a7' }}>+{calculateGroupTotals(filteredData).inLbs.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> / 
                      <span style={{ color: '#ef9a9a' }}> -{calculateGroupTotals(filteredData).outLbs.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 250]}
          />
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default StockReport;
